import * as XLSX from 'xlsx';

// Translation function type that accepts a key and returns a string
type TranslationFn = (key: string) => string;

interface SummaryUser {
  name: string;
  employment_type:
    | 'employee'
    | 'contractor'
    | 'intern'
    | 'part_time'
    | 'consultant';
  is_contractor: boolean;
  total_hours: number;
  total_billable_amount: number;
  total_cost_amount: number;
}

interface SummaryProject {
  total_hours: number;
  total_billable_amount: number;
  total_cost_amount: number;
  users: SummaryUser[];
}

interface SummaryClient {
  total_hours: number;
  total_billable_amount: number;
  total_cost_amount: number;
  projects: Record<string, SummaryProject>;
}

interface ReportTotals {
  total_hours: number;
  total_billable_amount: number;
  total_cost_amount: number;
  total_entries: number;
}

interface DetailedReportEntry {
  id: number;
  entry_date: string;
  hours_worked: number;
  description: string;
  is_billable: boolean;
  billable_rate: number;
  billable_amount: number;
  cost_amount: number;
  user: {
    id: number;
    full_name: string;
    employment_type:
      | 'employee'
      | 'contractor'
      | 'intern'
      | 'part_time'
      | 'consultant';
    is_contractor: boolean;
  };
  project: {
    id: number;
    name: string;
    client: {
      id: number;
      name: string;
    } | null;
  } | null;
}

interface SummaryReportData {
  report_type: 'summary';
  summary: Record<string, SummaryClient>;
  totals: ReportTotals;
}

interface DetailedReportData {
  report_type: 'detailed';
  time_entries: DetailedReportEntry[];
  totals: ReportTotals;
}

interface DateFilters {
  start_date: string;
  end_date: string;
}

/**
 * Export summary report to Excel
 */
export const exportSummaryToExcel = (
  reportData: SummaryReportData,
  t: TranslationFn,
  filters: DateFilters
): void => {
  const wb = XLSX.utils.book_new();

  // Create summary sheet data
  const summaryData: any[][] = [];

  // Header
  summaryData.push([t('reports.summaryReport')]);
  summaryData.push([`${t('reports.dateRange')}: ${filters.start_date} - ${filters.end_date}`]);
  summaryData.push([]);

  // Totals section
  summaryData.push([t('reports.totalHours'), reportData.totals.total_hours]);
  summaryData.push([t('reports.billableAmount'), formatCurrency(reportData.totals.total_billable_amount)]);
  summaryData.push([t('reports.costAmount'), formatCurrency(reportData.totals.total_cost_amount)]);
  summaryData.push([t('reports.entries'), reportData.totals.total_entries]);
  summaryData.push([]);

  // Details header
  summaryData.push([
    t('reports.client'),
    t('reports.project'),
    t('reports.user'),
    t('reports.contractor'),
    t('reports.hours'),
    t('reports.billableAmount'),
    t('reports.costAmount')
  ]);

  // Data rows with client totals
  Object.entries(reportData.summary).forEach(([clientName, clientData]) => {
    Object.entries(clientData.projects).forEach(([projectName, projectData]) => {
      if (projectData.users.length === 0) {
        summaryData.push([
          clientName,
          projectName,
          t('reports.noUsersAssigned'),
          '',
          projectData.total_hours,
          formatCurrency(projectData.total_billable_amount),
          formatCurrency(projectData.total_cost_amount)
        ]);
      } else {
        projectData.users.forEach((user) => {
          summaryData.push([
            clientName,
            projectName,
            user.name,
            user.is_contractor ? t('common.yes') : t('common.no'),
            user.total_hours,
            formatCurrency(user.total_billable_amount),
            formatCurrency(user.total_cost_amount)
          ]);
        });
      }
    });

    // Calculate user totals across all projects for this client
    const userTotalsMap: Record<string, { hours: number; billable: number; cost: number; isContractor: boolean }> = {};
    Object.entries(clientData.projects).forEach(([_, projectData]) => {
      const users = projectData.users || [];
      users.forEach((user) => {
        if (!userTotalsMap[user.name]) {
          userTotalsMap[user.name] = { hours: 0, billable: 0, cost: 0, isContractor: user.is_contractor };
        }
        userTotalsMap[user.name].hours += Number(user.total_hours) || 0;
        userTotalsMap[user.name].billable += Number(user.total_billable_amount) || 0;
        userTotalsMap[user.name].cost += Number(user.total_cost_amount) || 0;
      });
    });

    // Add user subtotal rows
    Object.entries(userTotalsMap).forEach(([userName, userTotals]) => {
      summaryData.push([
        userName,
        '',
        userTotals.isContractor ? t('reports.contractor') : '',
        '',
        userTotals.hours,
        formatCurrency(userTotals.billable),
        formatCurrency(userTotals.cost)
      ]);
    });

    // Add client total row
    summaryData.push([
      `${t('reports.clientTotal')}: ${clientName}`,
      '',
      '',
      '',
      clientData.total_hours,
      formatCurrency(clientData.total_billable_amount),
      formatCurrency(clientData.total_cost_amount)
    ]);
    summaryData.push([]); // Empty row after each client
  });

  // Add grand total row
  summaryData.push([
    t('reports.grandTotal'),
    '',
    '',
    '',
    reportData.totals.total_hours,
    formatCurrency(reportData.totals.total_billable_amount),
    formatCurrency(reportData.totals.total_cost_amount)
  ]);

  const ws = XLSX.utils.aoa_to_sheet(summaryData);

  // Set column widths
  ws['!cols'] = [
    { wch: 25 }, // Client
    { wch: 25 }, // Project
    { wch: 25 }, // User
    { wch: 12 }, // Contractor
    { wch: 12 }, // Hours
    { wch: 15 }, // Billable Amount
    { wch: 15 }, // Cost Amount
  ];

  XLSX.utils.book_append_sheet(wb, ws, t('reports.summary'));

  // Generate filename
  const filename = `summary_report_${filters.start_date}_${filters.end_date}.xlsx`;

  // Download
  XLSX.writeFile(wb, filename);
};

/**
 * Export detailed report to Excel
 */
export const exportDetailedToExcel = (
  reportData: DetailedReportData,
  t: TranslationFn,
  filters: DateFilters
): void => {
  const wb = XLSX.utils.book_new();

  // Create detailed sheet data
  const detailedData: any[][] = [];

  // Header
  detailedData.push([t('reports.detailedReport')]);
  detailedData.push([`${t('reports.dateRange')}: ${filters.start_date} - ${filters.end_date}`]);
  detailedData.push([]);

  // Totals section
  detailedData.push([t('reports.totalHours'), reportData.totals.total_hours]);
  detailedData.push([t('reports.billableAmount'), formatCurrency(reportData.totals.total_billable_amount)]);
  detailedData.push([t('reports.costAmount'), formatCurrency(reportData.totals.total_cost_amount)]);
  detailedData.push([t('reports.entries'), reportData.totals.total_entries]);
  detailedData.push([]);

  // Details header
  detailedData.push([
    t('reports.date'),
    t('reports.user'),
    t('reports.contractor'),
    t('reports.client'),
    t('reports.project'),
    t('reports.hours'),
    t('reports.billable'),
    t('reports.billAmount'),
    t('reports.costAmount'),
    t('reports.description')
  ]);

  // Group entries by client
  const entriesByClient = reportData.time_entries.reduce((acc, entry) => {
    const clientName = entry.project?.client?.name || t('reports.noClient');
    if (!acc[clientName]) {
      acc[clientName] = [];
    }
    acc[clientName].push(entry);
    return acc;
  }, {} as Record<string, DetailedReportEntry[]>);

  // Data rows with client and user totals
  Object.entries(entriesByClient).forEach(([clientName, entries]) => {
    // Calculate client totals
    const clientTotals = entries.reduce(
      (totals, entry) => ({
        hours: totals.hours + (entry.hours_worked || 0),
        billable: totals.billable + (entry.billable_amount || 0),
        cost: totals.cost + (entry.cost_amount || 0),
      }),
      { hours: 0, billable: 0, cost: 0 }
    );

    // Group entries by user within client
    const entriesByUser = entries.reduce((acc, entry) => {
      const userName = entry.user.full_name;
      if (!acc[userName]) {
        acc[userName] = { entries: [], isContractor: entry.user.is_contractor };
      }
      acc[userName].entries.push(entry);
      return acc;
    }, {} as Record<string, { entries: DetailedReportEntry[]; isContractor: boolean }>);

    // Client header row
    detailedData.push([`${t('reports.client')}: ${clientName}`, '', '', '', '', '', '', '', '', '']);

    // Add entries grouped by user
    Object.entries(entriesByUser).forEach(([userName, userData]) => {
      // Calculate user totals
      const userTotals = userData.entries.reduce(
        (totals, entry) => ({
          hours: totals.hours + (entry.hours_worked || 0),
          billable: totals.billable + (entry.billable_amount || 0),
          cost: totals.cost + (entry.cost_amount || 0),
        }),
        { hours: 0, billable: 0, cost: 0 }
      );

      // Add each entry
      userData.entries.forEach((entry) => {
        detailedData.push([
          entry.entry_date,
          entry.user.full_name,
          entry.user.is_contractor ? t('common.yes') : t('common.no'),
          entry.project?.client?.name || t('reports.noClient'),
          entry.project?.name || t('reports.noProject'),
          entry.hours_worked,
          entry.is_billable ? t('common.yes') : t('common.no'),
          formatCurrency(entry.billable_amount),
          formatCurrency(entry.cost_amount),
          entry.description
        ]);
      });

      // User subtotal row
      detailedData.push([
        userName,
        userData.isContractor ? t('reports.contractor') : '',
        '',
        '',
        '',
        userTotals.hours,
        '',
        formatCurrency(userTotals.billable),
        formatCurrency(userTotals.cost),
        ''
      ]);
    });

    // Client subtotal row
    detailedData.push([
      `${t('reports.clientTotal')}: ${clientName}`,
      '',
      '',
      '',
      '',
      clientTotals.hours,
      '',
      formatCurrency(clientTotals.billable),
      formatCurrency(clientTotals.cost),
      ''
    ]);
    detailedData.push([]); // Empty row after each client
  });

  // Grand total row
  detailedData.push([
    t('reports.grandTotal'),
    '',
    '',
    '',
    '',
    reportData.totals.total_hours,
    '',
    formatCurrency(reportData.totals.total_billable_amount),
    formatCurrency(reportData.totals.total_cost_amount),
    ''
  ]);

  const ws = XLSX.utils.aoa_to_sheet(detailedData);

  // Set column widths
  ws['!cols'] = [
    { wch: 12 }, // Date
    { wch: 25 }, // User
    { wch: 12 }, // Contractor
    { wch: 25 }, // Client
    { wch: 25 }, // Project
    { wch: 10 }, // Hours
    { wch: 10 }, // Billable
    { wch: 15 }, // Bill Amount
    { wch: 15 }, // Cost Amount
    { wch: 50 }, // Description
  ];

  XLSX.utils.book_append_sheet(wb, ws, t('reports.detailed'));

  // Generate filename
  const filename = `detailed_report_${filters.start_date}_${filters.end_date}.xlsx`;

  // Download
  XLSX.writeFile(wb, filename);
};

const formatCurrency = (amount: number | string | null | undefined): string => {
  const numAmount = Number(amount || 0);
  return `$${numAmount.toFixed(2)}`;
};
